/* =========================================================
   EGODRIFT HS Calculator
   Version: 2.1.0 — 2026-07-19 W3 slice 2: runtime strings via data-ego-hs-i18n (English defaults inline for dealer-embed self-containment)
   File: ego-hs-calculator.js
   Install path: assets/ego-hs-calculator.js

   Changelog
   ---------------------------------------------------------
   2.0.4
   - Added native Web Share API support via the Share Configuration button.
   - Kept Copy Configuration as Link as a fallback and secondary action.

   2.0.2
   - Keeps cell-count slider, labels, and fill aligned to the same index-based grid.
========================================================= */

(function () {
  'use strict';

  const VOLTS_PER_CELL = 3.8;
  const CELL_OPTIONS = [3, 4, 6, 7, 8, 10, 12, 14, 24];

  function toNumber(value) {
    const number = parseFloat(value);
    return Number.isFinite(number) ? number : null;
  }

  function formatNumber(value, decimals) {
    if (!Number.isFinite(value)) return '0';

    return value.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  }

  function formatInteger(value) {
    if (!Number.isFinite(value)) return '0';
    return String(Math.round(value));
  }

  function nearestCellOption(value) {
    const number = toNumber(value);

    if (!number) return 12;

    return CELL_OPTIONS.reduce(function (prev, curr) {
      return Math.abs(curr - number) < Math.abs(prev - number) ? curr : prev;
    });
  }

  function getThrottleColor(throttle) {
    if (!throttle) return '#0f5f2a';

    if (throttle < 60) return '#d92d20';
    if (throttle < 65) return '#c46a12';

    if (throttle < 80) {
      const factor = (throttle - 65) / 15;
      const dark = [15, 95, 42];
      const bright = [47, 214, 107];

      const mixed = dark.map(function (value, index) {
        return Math.round(value + (bright[index] - value) * factor);
      });

      return 'rgb(' + mixed.join(', ') + ')';
    }

    if (throttle <= 80) return '#2fd66b';

    if (throttle <= 85) {
      const factor = (throttle - 80) / 5;
      const bright = [47, 214, 107];
      const dark = [15, 95, 42];

      const mixed = bright.map(function (value, index) {
        return Math.round(value + (dark[index] - value) * factor);
      });

      return 'rgb(' + mixed.join(', ') + ')';
    }

    return '#d92d20';
  }

  var EGO_HS_I18N_DEFAULTS = {
    placeholderRatio: 'Enter gear ratio',
    placeholderCalculated: 'Calculated',
    hintRatioMode: 'Using manually entered gear ratio. Main gear and pinion are ignored for this calculation.',
    hintTeethMode: 'Main gear \u00f7 pinion = gear ratio.',
    shareTitle: 'EGODRIFT Headspeed Configuration',
    shareTextIntro: 'EGODRIFT headspeed configuration',
    sharePartThrottle: '% throttle',
    sharePartRatio: ':1 ratio',
    sharePartHeadspeed: ' rpm headspeed',
    shareCopied: 'Configuration link copied.',
    shareNativeUnavailable: 'Native sharing is not available here. Configuration link copied instead.',
    shareShared: 'Configuration shared.',
    shareFailed: 'Sharing failed. Configuration link copied instead.'
  };

  function readI18n(calculator) {
    const node = calculator.querySelector('[data-ego-hs-i18n]');
    let parsed = {};
    if (node) {
      try { parsed = JSON.parse(node.textContent); } catch (error) { parsed = {}; }
    }
    return Object.assign({}, EGO_HS_I18N_DEFAULTS, parsed);
  }

  function initCalculator(calculator) {
    const i18n = readI18n(calculator);
    const inputs = {
      cells: calculator.querySelector('[data-ego-hs-input="cells"]'),
      kv: calculator.querySelector('[data-ego-hs-input="kv"]'),
      mainGear: calculator.querySelector('[data-ego-hs-input="mainGear"]'),
      pinion: calculator.querySelector('[data-ego-hs-input="pinion"]'),
      ratio: calculator.querySelector('[data-ego-hs-input="ratio"]'),
      throttle: calculator.querySelector('[data-ego-hs-input="throttle"]'),
      gearingMode: calculator.querySelectorAll('[data-ego-hs-input="gearingMode"]'),
      cellIndex: calculator.querySelector('[data-ego-hs-input="cellIndex"]')
    };

    const outputs = {
      cellsLabel: calculator.querySelector('[data-ego-hs-output="cellsLabel"]'),
      voltagePerCell: calculator.querySelector('[data-ego-hs-output="voltagePerCell"]'),
      totalVoltage: calculator.querySelector('[data-ego-hs-output="totalVoltage"]'),
      throttleLabel: calculator.querySelector('[data-ego-hs-output="throttleLabel"]'),
      headspeed: calculator.querySelector('[data-ego-hs-output="headspeed"]'),
      headspeedLow: calculator.querySelector('[data-ego-hs-output="headspeedLow"]'),
      headspeedHigh385: calculator.querySelector('[data-ego-hs-output="headspeedHigh385"]'),
      headspeedHigh39: calculator.querySelector('[data-ego-hs-output="headspeedHigh39"]'),
      ratioDisplay: calculator.querySelector('[data-ego-hs-output="ratioDisplay"]'),
      motorRpm: calculator.querySelector('[data-ego-hs-output="motorRpm"]'),
      gearingHint: calculator.querySelector('[data-ego-hs-output="gearingHint"]'),
      throttleHighWarning: calculator.querySelector('[data-ego-hs-output="throttleHighWarning"]'),
      throttleLowWarning: calculator.querySelector('[data-ego-hs-output="throttleLowWarning"]'),
      shareStatus: calculator.querySelector('[data-ego-hs-output="shareStatus"]')
    };

    const copyButton = calculator.querySelector('[data-ego-hs-action="copyShareLink"]');
    const shareButton = calculator.querySelector('[data-ego-hs-action="shareConfiguration"]');

    const resetThrottleButton = calculator.querySelector(
      '[data-ego-hs-action="resetThrottle"]'
    );

    function getGearingMode() {
      const checkedMode = Array.from(inputs.gearingMode).find(function (input) {
        return input.checked;
      });

      return checkedMode ? checkedMode.value : 'teeth';
    }

    function setGearingMode(mode) {
      inputs.gearingMode.forEach(function (input) {
        input.checked = input.value === mode;
      });
    }

    function setCellProgress(cellIndex) {
      if (!inputs.cellIndex) return;

      const maxIndex = CELL_OPTIONS.length - 1;
      const normalizedIndex = Math.max(0, Math.min(maxIndex, cellIndex));
      const progress = (normalizedIndex / maxIndex) * 100;

      inputs.cellIndex.style.setProperty('--ego-hs-cell-progress', progress + '%');
    }

    function setCells(value) {
      const cellValue = nearestCellOption(value);
      const cellIndex = CELL_OPTIONS.indexOf(cellValue);

      if (inputs.cells) {
        inputs.cells.value = cellValue;
      }

      if (inputs.cellIndex && cellIndex >= 0) {
        inputs.cellIndex.value = cellIndex;
        inputs.cellIndex.setAttribute('aria-valuetext', cellValue + 'S');
        setCellProgress(cellIndex);
      }

      if (outputs.cellsLabel) {
        outputs.cellsLabel.textContent = cellValue;
      }
    }

    function getRatio() {
      const mode = getGearingMode();
      const mainGear = toNumber(inputs.mainGear && inputs.mainGear.value);
      const pinion = toNumber(inputs.pinion && inputs.pinion.value);
      const ratio = toNumber(inputs.ratio && inputs.ratio.value);

      calculator.classList.toggle('is-ratio-mode', mode === 'ratio');

      if (inputs.ratio) {
        inputs.ratio.placeholder = mode === 'ratio' ? i18n.placeholderRatio : i18n.placeholderCalculated;
      }

      if (mode === 'ratio') {
        if (outputs.gearingHint) {
          outputs.gearingHint.textContent = i18n.hintRatioMode;
        }

        return ratio;
      }

      if (outputs.gearingHint) {
        outputs.gearingHint.textContent = i18n.hintTeethMode;
      }

      if (mainGear && pinion) {
        const calculatedRatio = mainGear / pinion;

        if (inputs.ratio) {
          inputs.ratio.value = calculatedRatio.toFixed(2);
        }

        return calculatedRatio;
      }

      if (inputs.ratio) {
        inputs.ratio.value = '';
      }

      return null;
    }

    function calculateHeadspeed(kv, cells, throttle, ratio, voltsPerCell) {
      if (!kv || !cells || !throttle || !ratio) return 0;

      const totalVoltage = cells * voltsPerCell;
      const motorRpm = kv * totalVoltage * (throttle / 100);

      return motorRpm / ratio;
    }

    function updateThrottleState(throttle) {
      calculator.classList.remove(
        'is-throttle-low',
        'is-throttle-orange',
        'is-throttle-good',
        'is-throttle-ideal',
        'is-throttle-upper',
        'is-throttle-high'
      );

      if (outputs.throttleHighWarning) {
        outputs.throttleHighWarning.hidden = true;
      }

      if (outputs.throttleLowWarning) {
        outputs.throttleLowWarning.hidden = true;
      }

      if (!throttle) return;

      calculator.style.setProperty('--ego-hs-throttle-color', getThrottleColor(throttle));

      if (throttle < 60) {
        calculator.classList.add('is-throttle-low');

        if (outputs.throttleLowWarning) {
          outputs.throttleLowWarning.hidden = false;
        }
      } else if (throttle < 65) {
        calculator.classList.add('is-throttle-orange');
      } else if (throttle > 85) {
        calculator.classList.add('is-throttle-high');

        if (outputs.throttleHighWarning) {
          outputs.throttleHighWarning.hidden = false;
        }
      } else if (throttle >= 78 && throttle <= 82) {
        calculator.classList.add('is-throttle-ideal');
      } else if (throttle > 82 && throttle <= 85) {
        calculator.classList.add('is-throttle-upper');
      } else {
        calculator.classList.add('is-throttle-good');
      }
    }

    function buildShareUrl() {
      const params = new URLSearchParams();
      const mode = getGearingMode();

      params.set('cells', inputs.cells ? inputs.cells.value : '');
      params.set('kv', inputs.kv ? inputs.kv.value : '');
      params.set('throttle', inputs.throttle ? inputs.throttle.value : '');
      params.set('mode', mode);

      if (mode === 'ratio') {
        params.set('ratio', inputs.ratio ? inputs.ratio.value : '');
      } else {
        params.set('main', inputs.mainGear ? inputs.mainGear.value : '');
        params.set('pinion', inputs.pinion ? inputs.pinion.value : '');
      }

      return window.location.origin + window.location.pathname + '?' + params.toString();
    }

    function showShareStatus(message, duration) {
      if (!outputs.shareStatus) return;

      outputs.shareStatus.textContent = message;
      outputs.shareStatus.hidden = false;

      window.clearTimeout(outputs.shareStatus.egoHsTimer);
      outputs.shareStatus.egoHsTimer = window.setTimeout(function () {
        outputs.shareStatus.hidden = true;
      }, duration || 3000);
    }

    function buildShareText() {
      const cells = inputs.cells && inputs.cells.value ? inputs.cells.value + 'S' : '';
      const kv = inputs.kv && inputs.kv.value ? inputs.kv.value + 'Kv' : '';
      const throttle = inputs.throttle && inputs.throttle.value ? inputs.throttle.value + i18n.sharePartThrottle : '';
      const ratio = outputs.ratioDisplay && outputs.ratioDisplay.textContent
        ? outputs.ratioDisplay.textContent + i18n.sharePartRatio
        : '';
      const headspeed = outputs.headspeed && outputs.headspeed.textContent
        ? outputs.headspeed.textContent + i18n.sharePartHeadspeed
        : '';

      const details = [cells, kv, throttle, ratio, headspeed].filter(Boolean);

      return details.length
        ? i18n.shareTextIntro + ': ' + details.join(', ') + '.'
        : i18n.shareTextIntro + '.';
    }

    function buildNativeShareData() {
      return {
        title: i18n.shareTitle,
        text: buildShareText(),
        url: buildShareUrl()
      };
    }

    function canNativeShare(shareData) {
      if (!navigator.share) return false;

      if (!navigator.canShare) return true;

      try {
        return navigator.canShare(shareData);
      } catch (error) {
        return false;
      }
    }

    function copyShareLink(successMessage) {
      const url = buildShareUrl();

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(function () {
          showShareStatus(successMessage || i18n.shareCopied);
        }).catch(function () {
          showShareStatus(url, 8000);
        });

        return;
      }

      showShareStatus(url, 8000);
    }

    function shareConfiguration() {
      const shareData = buildNativeShareData();

      if (!canNativeShare(shareData)) {
        copyShareLink(i18n.shareNativeUnavailable);
        return;
      }

      navigator.share(shareData).then(function () {
        showShareStatus(i18n.shareShared);
      }).catch(function (error) {
        if (error && error.name === 'AbortError') return;

        copyShareLink(i18n.shareFailed);
      });
    }

    function loadFromUrl() {
      const params = new URLSearchParams(window.location.search);

      if (params.has('cells')) {
        setCells(params.get('cells'));
      }

      if (params.has('kv') && inputs.kv) {
        inputs.kv.value = params.get('kv');
      }

      if (params.has('throttle') && inputs.throttle) {
        inputs.throttle.value = params.get('throttle');
      }

      if (params.get('mode') === 'ratio') {
        setGearingMode('ratio');

        if (params.has('ratio') && inputs.ratio) {
          inputs.ratio.value = params.get('ratio');
        }
      } else {
        setGearingMode('teeth');

        if (params.has('main') && inputs.mainGear) {
          inputs.mainGear.value = params.get('main');
        }

        if (params.has('pinion') && inputs.pinion) {
          inputs.pinion.value = params.get('pinion');
        }
      }
    }

    function selectOnFocus(input) {
      if (!input) return;

      input.addEventListener('focus', function () {
        input.dataset.previousValue = input.value;

        window.setTimeout(function () {
          input.select();
        }, 0);
      });

      input.addEventListener('pointerdown', function () {
        input.dataset.previousValue = input.value;
      });

      input.addEventListener('click', function () {
        window.setTimeout(function () {
          input.select();
        }, 0);
      });
    }

    function update() {
      const cells = toNumber(inputs.cells && inputs.cells.value);
      const kv = toNumber(inputs.kv && inputs.kv.value);
      const throttle = toNumber(inputs.throttle && inputs.throttle.value);
      const ratio = getRatio();

      const totalVoltage = cells ? cells * VOLTS_PER_CELL : 0;
      const throttleDecimal = throttle ? throttle / 100 : 0;
      const motorRpm = kv && totalVoltage && throttleDecimal
        ? kv * totalVoltage * throttleDecimal
        : 0;

      const headspeed = calculateHeadspeed(kv, cells, throttle, ratio, 3.8);
      const headspeedLow = calculateHeadspeed(kv, cells, throttle, ratio, 3.7);
      const headspeedHigh385 = calculateHeadspeed(kv, cells, throttle, ratio, 3.85);
      const headspeedHigh39 = calculateHeadspeed(kv, cells, throttle, ratio, 3.9);

      updateThrottleState(throttle);

      if (outputs.cellsLabel) {
        outputs.cellsLabel.textContent = cells ? formatInteger(cells) : '0';
      }

      if (outputs.voltagePerCell) {
        outputs.voltagePerCell.textContent = formatNumber(VOLTS_PER_CELL, 1);
      }

      if (outputs.totalVoltage) {
        outputs.totalVoltage.textContent = formatNumber(totalVoltage, 1);
      }

      if (outputs.throttleLabel) {
        outputs.throttleLabel.textContent = throttle ? formatInteger(throttle) : '0';
      }

      if (outputs.ratioDisplay) {
        outputs.ratioDisplay.textContent = ratio ? formatNumber(ratio, 2) : '0.00';
      }

      if (outputs.motorRpm) {
        outputs.motorRpm.textContent = formatInteger(motorRpm);
      }

      if (outputs.headspeed) {
        outputs.headspeed.textContent = formatInteger(headspeed);
      }

      if (outputs.headspeedLow) {
        outputs.headspeedLow.textContent = formatInteger(headspeedLow);
      }

      if (outputs.headspeedHigh385) {
        outputs.headspeedHigh385.textContent = formatInteger(headspeedHigh385);
      }

      if (outputs.headspeedHigh39) {
        outputs.headspeedHigh39.textContent = formatInteger(headspeedHigh39);
      }
    }

    selectOnFocus(inputs.kv);
    selectOnFocus(inputs.mainGear);
    selectOnFocus(inputs.pinion);
    selectOnFocus(inputs.ratio);

    ['kv', 'mainGear', 'pinion', 'ratio', 'throttle'].forEach(function (key) {
      if (!inputs[key]) return;

      inputs[key].addEventListener('input', update);
      inputs[key].addEventListener('change', update);
    });

    inputs.gearingMode.forEach(function (input) {
      input.addEventListener('change', update);
    });

    if (inputs.cellIndex) {
      function updateCellsFromIndex() {
        const rawIndex = toNumber(inputs.cellIndex.value);
        const maxIndex = CELL_OPTIONS.length - 1;
        const index = Number.isFinite(rawIndex)
          ? Math.max(0, Math.min(maxIndex, Math.round(rawIndex)))
          : CELL_OPTIONS.indexOf(12);
        const cellValue = CELL_OPTIONS[index] || 12;

        setCells(cellValue);
        update();
      }

      inputs.cellIndex.addEventListener('input', updateCellsFromIndex);
      inputs.cellIndex.addEventListener('change', updateCellsFromIndex);
    }

    if (copyButton) {
      copyButton.addEventListener('click', function () {
        copyShareLink();
      });
    }

    if (shareButton) {
      shareButton.addEventListener('click', shareConfiguration);
    }

    if (resetThrottleButton && inputs.throttle) {
      resetThrottleButton.addEventListener('click', function () {
        const defaultThrottle = parseInt(resetThrottleButton.dataset.defaultThrottle, 10) || 80;

        inputs.throttle.value = defaultThrottle;
        update();
      });
    }

    setCells(inputs.cells ? inputs.cells.value : 12);
    loadFromUrl();
    update();
  }

  function init() {
    document.querySelectorAll('[data-ego-hs-calculator]').forEach(initCalculator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    const calculator = event.target.querySelector('[data-ego-hs-calculator]');

    if (calculator) {
      initCalculator(calculator);
    }
  });
})();